import { useCallback, useEffect, useRef, useState } from 'react';

import api from '@/services/api';
import { getApiErrorCode, getApiErrorMessage } from '@/utils/api-error';
import { CheckoutPaymentIntentData } from '@/types/checkout';

export type CheckoutPaymentIntentState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; data: CheckoutPaymentIntentData }
  /** 409 `NO_PAYMENT_METHOD_AVAILABLE` (T2.2) — não é erro, é "mostre o plano B". */
  | { status: 'unavailable'; message: string }
  | { status: 'error'; message: string };

const UNAVAILABLE_CODE = 'NO_PAYMENT_METHOD_AVAILABLE';

/**
 * `POST /charge/:id/public/payment-intent` (T2.2, T5.1) — chamado **uma vez**
 * ao abrir o checkout automático (plano 4.4: "só quando o cliente abre o
 * checkout"), nunca em loop (o endpoint tem rate limit, 10/min por IP). Uma
 * segunda chamada só acontece se o chamador pedir `regenerate()` (QR do Pix
 * vencido — plano 6.7).
 *
 * `requestedForRef` é a trava contra dupla chamada no mesmo
 * `(chargeId, attempt)` — cobre o StrictMode do React (que monta/desmonta o
 * efeito duas vezes em dev) sem precisar de um cache externo.
 */
export function useCheckoutPaymentIntent(chargeId: string, enabled: boolean) {
  const [state, setState] = useState<CheckoutPaymentIntentState>({ status: 'idle' });
  const [attempt, setAttempt] = useState(0);
  const requestedForRef = useRef<string | null>(null);
  // Montado de verdade — não um `cancelled` por execução do efeito: no
  // StrictMode a 1ª execução é desmontada e a 2ª é barrada pela trava acima,
  // então descartar a resposta da 1ª deixava o checkout em loading para sempre.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled || !chargeId) return;

    const key = `${chargeId}:${attempt}`;
    if (requestedForRef.current === key) return;
    requestedForRef.current = key;

    // Só a resposta do pedido mais recente vale (regenerate troca a chave).
    const cancelled = () => !mountedRef.current || requestedForRef.current !== key;
    setState({ status: 'loading' });

    api
      .post(`/charge/${chargeId}/public/payment-intent`)
      .then((res) => {
        if (cancelled()) return;
        setState({ status: 'ready', data: res.data as CheckoutPaymentIntentData });
      })
      .catch((err) => {
        if (cancelled()) return;
        const code = getApiErrorCode(err);
        const message = getApiErrorMessage(
          err,
          'Não foi possível preparar o pagamento agora. Tente de novo em instantes.',
        );
        if (code === UNAVAILABLE_CODE) {
          setState({ status: 'unavailable', message });
        } else {
          setState({ status: 'error', message });
        }
      });
  }, [chargeId, enabled, attempt]);

  /** QR do Pix vencido (plano 6.7): pede um `PaymentIntent` novo. */
  const regenerate = useCallback(() => {
    requestedForRef.current = null;
    setAttempt((a) => a + 1);
  }, []);

  return { state, regenerate };
}
